import { useState } from "react";
import { Button, Input, InputNumber, Modal, Radio, Select, Space, Switch } from "antd";

import { WIDGET_TYPE_OPTIONS, getWidgetType } from "./widgetRegistry";
import CustomColumnsEditor from "./customColumnsEditor";
import PivotEditor from "./pivotEditor";
import PeriodCalcsEditor from "./periodCalcsEditor";
import {
  CHART_COMPARE_OPTIONS,
  COMPARE_SHOW_OPTIONS,
  DEFAULT_PIVOT,
  SCALE_OPTIONS,
  TABLE_LAYOUTS,
  TABLE_LAYOUT_OPTIONS,
  getChartCompare,
  getDefaultYAxisTitle,
  getWidgetPeriods,
  isGroupedLayout,
  sourceKeyOf,
} from "./reportingUtils";

const Label = ({ children }) => <div style={{ fontWeight: 600, margin: "16px 0 6px" }}>{children}</div>;

const WIDTH_OPTIONS = [
  { value: 12, label: "Half width" },
  { value: 24, label: "Full width" },
];

const PERIOD_KINDS = [
  { kind: "year", label: "All years" },
  { kind: "quarter", label: "All quarters" },
  { kind: "month", label: "All months" },
];

// One option group per year: the year itself, then its quarters or months.
function buildPeriodOptions(periodCats) {
  const groups = new Map();
  periodCats.forEach((c) => {
    if (!groups.has(c.year)) groups.set(c.year, []);
    groups.get(c.year).push({
      value: c.key,
      label: c.kind === "year" ? `${c.label} (full year)` : c.label,
      kind: c.kind,
    });
  });
  return [...groups].map(([year, options]) => ({
    label: year,
    // Full year first within its group
    options: [...options.filter((o) => o.kind === "year"), ...options.filter((o) => o.kind !== "year")],
  }));
}

// Render with key={widget_id} so the draft resets for each widget.
export default function WidgetSettingsModal({
  widget,
  globalFmt,
  periodCats = [],
  sourceOptions = [],
  onCancel,
  onSave,
}) {
  const [type, setType] = useState(widget?.type ?? "line");
  const [draft, setDraft] = useState(() => ({
    ...(widget?.settings || {}),
    format: { ...(widget?.settings?.format || {}) },
  }));

  if (!widget) return null;

  const def = getWidgetType(type);
  const fmt = { ...globalFmt, ...draft.format };

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setFormat = (patch) =>
    setDraft((d) => {
      const format = { ...d.format, ...patch };
      Object.keys(format).forEach((k) => format[k] === undefined && delete format[k]);
      return { ...d, format };
    });

  // Stored in period order so the setting reads naturally; keys that no longer
  // exist (e.g. a collapsed year's months) drop out on the next save.
  const periodOptions = buildPeriodOptions(periodCats);
  const selectedSet = new Set((draft.periods || []).map(String));
  const selectedPeriods = periodCats.filter((c) => selectedSet.has(c.key)).map((c) => c.key);
  const setPeriods = (keys) => {
    const next = new Set(keys);
    set({ periods: periodCats.filter((c) => next.has(c.key)).map((c) => c.key) });
  };

  // Only the business cases/scenarios this chart or table uses can be the base.
  const usedSources = new Set(widget.series.map(sourceKeyOf).filter(Boolean));
  const compareOptions = sourceOptions.flatMap((g) => g.options).filter((o) => usedSources.has(o.value));
  const grouped = isGroupedLayout(draft.tableLayout);
  const custom = def.kind === "table" && draft.tableLayout === TABLE_LAYOUTS.custom;
  const pivot = def.kind === "table" && draft.tableLayout === TABLE_LAYOUTS.pivot;
  const chartCompareMode = getChartCompare({ settings: draft }, def)?.mode ?? "valuesAndDiff";

  return (
    <Modal
      open
      width={custom ? 860 : pivot ? 760 : 520}
      title="Chart settings"
      okText="Save"
      onCancel={onCancel}
      onOk={() => onSave(widget, { type, settings: draft })}
    >
      <Label>Type</Label>
      <Select style={{ width: "100%" }} value={type} options={WIDGET_TYPE_OPTIONS} onChange={setType} />

      <Label>Width</Label>
      <Radio.Group
        optionType="button"
        options={WIDTH_OPTIONS}
        value={draft.span === 24 ? 24 : 12}
        onChange={(e) => set({ span: e.target.value })}
      />

      <Label>Title</Label>
      <Input
        allowClear
        placeholder={def.label}
        value={draft.title}
        onChange={(e) => set({ title: e.target.value })}
      />

      {/* Custom-column tables choose a period per column instead */}
      {!custom && (
        <>
          <Label>Periods</Label>
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder="All years"
            options={periodOptions}
            value={selectedPeriods}
            onChange={setPeriods}
          />
          <Space size={4} wrap style={{ marginTop: 6 }}>
            {PERIOD_KINDS.filter(({ kind }) => periodCats.some((c) => c.kind === kind)).map(({ kind, label }) => (
              <Button
                key={kind}
                size="small"
                onClick={() => setPeriods(periodCats.filter((c) => c.kind === kind).map((c) => c.key))}
              >
                {label}
              </Button>
            ))}
          </Space>
        </>
      )}

      {def.kind === "table" && (
        <>
          <Label>Layout</Label>
          <Radio.Group
            value={draft.tableLayout ?? TABLE_LAYOUTS.series}
            onChange={(e) => set({ tableLayout: e.target.value })}
          >
            <Space direction="vertical" size={4}>
              {TABLE_LAYOUT_OPTIONS.map((o) => (
                <Radio key={o.value} value={o.value}>
                  {o.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          {grouped && (
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.65 }}>
              {pivot ? "" : "One row per account. "}
              Add the same accounts for each business case or scenario to compare them.
            </div>
          )}

          {pivot && (
            <>
              <Label>Rows and columns</Label>
              <PivotEditor pivot={draft.pivot ?? DEFAULT_PIVOT} onChange={(next) => set({ pivot: next })} />

              <Label>Changes and CAGR between periods</Label>
              <PeriodCalcsEditor
                calcs={draft.periodCalcs || []}
                onChange={(periodCalcs) => set({ periodCalcs })}
                periodOptions={periodOptions}
                periodCats={periodCats}
                shownCats={getWidgetPeriods(periodCats, { settings: draft })}
              />
            </>
          )}

          {!pivot && (
            <div style={{ marginTop: 12 }}>
              <Switch size="small" checked={!!draft.transpose} onChange={(checked) => set({ transpose: checked })} />
              <span style={{ marginLeft: 8 }}>Swap rows and columns</span>
              {custom && draft.transpose && (
                <div style={{ marginTop: 4, fontSize: 12, opacity: 0.65 }}>
                  The columns below become rows, and each account becomes a column.
                </div>
              )}
            </div>
          )}

          {custom ? (
            <>
              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.65 }}>
                One row per account added to the table. Each column picks its own business case/scenario and
                period, so the accounts are added without one.
              </div>
              <Label>Columns</Label>
              <CustomColumnsEditor
                columns={draft.customColumns || []}
                onChange={(customColumns) => set({ customColumns })}
                sourceOptions={sourceOptions}
                periodOptions={periodOptions}
                periodCats={periodCats}
              />
            </>
          ) : (
            <>
              <Label>Compare with</Label>
              <Select
                allowClear
                style={{ width: "100%" }}
                placeholder={grouped ? "No comparison" : "Choose a grouped layout above to compare"}
                disabled={!grouped || compareOptions.length < 2}
                value={grouped ? draft.compareTo || undefined : undefined}
                options={compareOptions}
                onChange={(value) => set({ compareTo: value ?? null })}
              />
              {grouped && compareOptions.length < 2 && (
                <div style={{ marginTop: 6, fontSize: 12, opacity: 0.65 }}>
                  Add series from at least two business cases or scenarios to compare them.
                </div>
              )}
              {grouped && draft.compareTo && (
                <>
                  <div style={{ marginTop: 6, fontSize: 12, opacity: 0.65 }}>
                    Adds Δ columns for the others: their value minus the base, and the change in % of the base.
                  </div>
                  <Radio.Group
                    style={{ marginTop: 8 }}
                    optionType="button"
                    size="small"
                    options={COMPARE_SHOW_OPTIONS}
                    value={draft.compareShow || "both"}
                    onChange={(e) => set({ compareShow: e.target.value })}
                  />
                </>
              )}
            </>
          )}
        </>
      )}

      {def.kind === "chart" && (
        <>
          <Label>Y-axis title</Label>
          <Input
            allowClear
            placeholder={
              draft.compareTo && chartCompareMode === "pct"
                ? "Change (%)"
                : draft.compareTo && chartCompareMode === "diff"
                  ? "Difference"
                  : getDefaultYAxisTitle(fmt)
            }
            value={draft.yTitle}
            onChange={(e) => set({ yTitle: e.target.value })}
          />

          <Label>Show values on chart</Label>
          <Switch checked={!!draft.dataLabels} onChange={(checked) => set({ dataLabels: checked })} />

          <Label>Compare with</Label>
          <Select
            allowClear
            style={{ width: "100%" }}
            placeholder="No comparison"
            disabled={compareOptions.length < 2}
            value={draft.compareTo || undefined}
            options={compareOptions}
            onChange={(value) => set({ compareTo: value ?? null })}
          />
          {compareOptions.length < 2 ? (
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.65 }}>
              Add series from at least two business cases or scenarios to compare them.
            </div>
          ) : (
            draft.compareTo && (
              <>
                <Radio.Group
                  style={{ marginTop: 8 }}
                  optionType="button"
                  size="small"
                  options={CHART_COMPARE_OPTIONS.map((o) => ({
                    ...o,
                    disabled: def.stacked && o.value === "valuesAndDiff",
                  }))}
                  value={chartCompareMode}
                  onChange={(e) => set({ chartCompare: e.target.value })}
                />
                <div style={{ marginTop: 6, fontSize: 12, opacity: 0.65 }}>
                  Δ series show each other business case or scenario minus the base, for the accounts the base
                  also has.
                  {def.stacked && " Stacked bars show the difference only."}
                </div>
              </>
            )
          )}
        </>
      )}

      <Label>Scale</Label>
      <Radio.Group
        options={SCALE_OPTIONS}
        value={Number(fmt.sizingDivisor) || 1}
        onChange={(e) => setFormat({ sizingDivisor: e.target.value })}
      />

      <Label>Decimals</Label>
      <InputNumber
        min={0}
        max={6}
        value={fmt.decimals}
        onChange={(v) => setFormat({ decimals: v == null ? undefined : Number(v) })}
      />

      <div style={{ marginTop: 20 }}>
        <Button onClick={() => set({ format: {} })}>Use organization number format</Button>
      </div>
    </Modal>
  );
}